import { createSlice } from '@reduxjs/toolkit';

const envCooldownMinutes = process.env.REACT_APP_BULK_NOTIFICATION_COOLDOWN_MINUTES;
const cooldownMinutes = envCooldownMinutes ? parseInt(envCooldownMinutes, 10) : 5;

const initialState = {
  snackbarState: {
    open: false,
    message: '',
    severity: 'info',
  },
  bulkRoomUsers: {
    currentUsers: [],
    roomName: null,
    message: null,
    lastUpdated: null,
  },
  bulkNotificationCooldown: {
    lastNotificationTime: null,
    cooldownDuration: cooldownMinutes * 60 * 1000,
  },
  messages: [],
};

/* eslint no-param-reassign: ["error", { "ignorePropertyModificationsFor": ["state"] }] */
const webSocketSlice = createSlice({
  name: 'webSocket',
  initialState,
  reducers: {
    showSnackbar: (state, action) => {
      state.snackbarState = {
        open: true,
        message: action.payload.message,
        severity: action.payload.severity || 'info',
      };
    },
    closeSnackbar: (state) => {
      state.snackbarState.open = false;
    },
    updateBulkRoomUsers: (state, action) => {
      const { currentUsers, roomName, message } = action.payload;

      const uniqueUsers = [];
      const seenUserIds = new Set();

      if (currentUsers && Array.isArray(currentUsers)) {
        currentUsers.forEach((user) => {
          if (user && user.userId && !seenUserIds.has(user.userId)) {
            seenUserIds.add(user.userId);
            uniqueUsers.push(user);
          }
        });
      }

      state.bulkRoomUsers = {
        currentUsers: uniqueUsers,
        roomName,
        message,
        lastUpdated: new Date().toISOString(),
      };
    },
    addUserToBulkRoom: (state, action) => {
      const { newUser } = action.payload;
      if (state.bulkRoomUsers.currentUsers) {
        const existingUserIndex = state.bulkRoomUsers.currentUsers.findIndex(
          (u) => u.userId === newUser.userId
        );
        if (existingUserIndex === -1) {
          state.bulkRoomUsers.currentUsers.push(newUser);
        }
        state.bulkRoomUsers.lastUpdated = new Date().toISOString();
      }
    },
    removeUserFromBulkRoom: (state, action) => {
      const { leftUser } = action.payload;
      if (state.bulkRoomUsers.currentUsers) {
        state.bulkRoomUsers.currentUsers = state.bulkRoomUsers.currentUsers.filter(
          (user) => user.userId !== leftUser.userId
        );
        state.bulkRoomUsers.lastUpdated = new Date().toISOString();
      }
    },
    clearBulkRoomUsers: (state) => {
      state.bulkRoomUsers = {
        currentUsers: [],
        roomName: null,
        message: null,
        lastUpdated: null,
      };
    },
    updateBulkNotificationCooldown: (state, action) => {
      const { lastNotificationTime, cooldownDuration } = action.payload;
      state.bulkNotificationCooldown = {
        lastNotificationTime,
        cooldownDuration: cooldownDuration || state.bulkNotificationCooldown.cooldownDuration,
      };
    },
    setBulkNotificationTime: (state, action) => {
      state.bulkNotificationCooldown.lastNotificationTime = action.payload;
    },
    addWebSocketMessage: (state, action) => {
      const { event, data, timestamp } = action.payload;
      state.messages.push({
        event,
        data,
        timestamp: timestamp || new Date().toISOString(),
      });
    },
    clearWebSocketMessages: (state) => {
      state.messages = [];
    },
  },
});

export const webSocketActions = webSocketSlice.actions;
export default webSocketSlice.reducer;

export const selectCanShowBulkNotification = (state) => {
  const cooldown = state.webSocket?.bulkNotificationCooldown;
  if (!cooldown?.lastNotificationTime) return true;

  const now = new Date().getTime();
  const lastNotificationTime = new Date(cooldown.lastNotificationTime).getTime();
  const timeSinceLastNotification = now - lastNotificationTime;

  return timeSinceLastNotification >= cooldown.cooldownDuration;
};
